import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["value"],
    _excluded2 = ["data", "columns", "emptyDescription", "emptyIcon", "tableLayout", "loading", "sorting"];

/* eslint-disable react/no-array-index-key */
import React, { forwardRef } from 'react';
import cx from 'classnames';
import { Search } from '@mediahuis/chameleon-theme-wl/icons';
import system from '../../system';
import useSort from '../../hooks/useSort';
import Paper from '../Paper';
import Box from '../Box';
import Td from './Td';
import Tr from './Tr';
import Th from './Th';
import Empty from './Empty';
import styles from './index.module.scss';
var SYSTEM_PROPS = ['marginProps', 'floatProps', 'flexProps'];
var DEFAULT_TEXT_ALIGN = 'left';

var normalizeFieldValue = function normalizeFieldValue(fieldValue) {
  if (fieldValue !== null && typeof fieldValue === 'object' && ! /*#__PURE__*/React.isValidElement(fieldValue)) {
    var value = fieldValue.value,
        rest = _objectWithoutPropertiesLoose(fieldValue, _excluded);

    return {
      value: value,
      options: rest
    };
  }

  return {
    value: fieldValue,
    options: {}
  };
};

var Table = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _cx;

  var data = _ref.data,
      columns = _ref.columns,
      emptyDescription = _ref.emptyDescription,
      emptyIcon = _ref.emptyIcon,
      tableLayout = _ref.tableLayout,
      loading = _ref.loading,
      sorting = _ref.sorting,
      props = _objectWithoutPropertiesLoose(_ref, _excluded2);

  var _system = system(props, SYSTEM_PROPS),
      className = _system[0],
      style = _system[1],
      rest = _system[2];

  var _useSort = useSort(data),
      sortData = _useSort[0],
      onRequestSort = _useSort[1],
      sortConfig = _useSort[2];

  var tableClassnames = cx((_cx = {}, _cx[styles.table] = true, _cx[styles.auto] = tableLayout === 'auto', _cx[styles.fixed] = tableLayout === 'fixed', _cx[styles.inherit] = tableLayout === 'inherit', _cx)); // keyboard interaction for ENTER and SPACEBAR

  var handleKeyDown = function handleKeyDown(keyCode) {
    return keyCode === 13 || keyCode === 32;
  }; // handles default sorting direction


  var requestSortConfig = function requestSortConfig(field, customSort) {
    var sortDirection = 'ascending';

    if (sortConfig !== null && sortConfig.field === field && sortConfig.sortDirection === 'ascending') {
      sortDirection = 'descending';
    }

    onRequestSort(field, sortDirection, customSort);
  };

  return /*#__PURE__*/React.createElement(Paper, {
    className: cx(styles.root, className)
  }, /*#__PURE__*/React.createElement(Box, _extends({
    color: "grey90",
    as: "table",
    width: "full",
    elevation: 1,
    display: "table",
    style: style,
    className: tableClassnames
  }, rest, {
    ref: ref
  }), /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement(Tr, {
    bgColor: "grey10",
    borderBottomColor: "grey20",
    borderBottomStyle: "solid",
    borderBottomWidth: 1
  }, columns && columns.map(function (_ref2) {
    var title = _ref2.title,
        field = _ref2.field,
        textAlign = _ref2.textAlign,
        colSpan = _ref2.colSpan,
        width = _ref2.width,
        ellipsis = _ref2.ellipsis,
        sortable = _ref2.sortable,
        customSort = _ref2.customSort;
    return /*#__PURE__*/React.createElement(Th, {
      key: title,
      textAlign: textAlign || DEFAULT_TEXT_ALIGN,
      colSpan: colSpan,
      width: width,
      ellipsis: ellipsis,
      sortable: sorting && sortable !== false,
      sortDirection: field === sortConfig.field ? sortConfig.sortDirection : null,
      onClick: function onClick() {
        return sorting && sortable !== false && requestSortConfig(field, customSort);
      },
      onKeyDown: function onKeyDown(e) {
        return sorting && sortable !== false && handleKeyDown(e.keyCode) && requestSortConfig(field, customSort);
      }
    }, title);
  }))), /*#__PURE__*/React.createElement("tbody", null, data && !loading && (sorting ? sortData : data).map(function (d, i) {
    return /*#__PURE__*/React.createElement(Tr, {
      borderBottomColor: "grey20",
      borderBottomStyle: "solid",
      borderBottomWidth: 1,
      key: "" + i
    }, columns && columns.map(function (_ref3, j) {
      var field = _ref3.field,
          textAlign = _ref3.textAlign;
      var fieldObj = normalizeFieldValue(d[field]);

      if (fieldObj.options.colSpan === 0) {
        return null;
      }

      return /*#__PURE__*/React.createElement(Td, _extends({
        key: i + "-" + j + "-" + d[field] + "-" + field,
        textAlign: textAlign || DEFAULT_TEXT_ALIGN
      }, fieldObj.options), fieldObj.value || '-');
    }));
  }), !data && !loading && /*#__PURE__*/React.createElement(Empty, {
    colSpan: columns ? columns.length : 1,
    text: emptyDescription,
    icon: emptyIcon
  }), loading && [1, 2, 3, 4].map(function (key) {
    return /*#__PURE__*/React.createElement(Tr, {
      borderBottomColor: "grey20",
      borderBottomStyle: "solid",
      borderBottomWidth: 1,
      key: "" + key
    }, columns && columns.map(function (_ref4, j) {
      var field = _ref4.field,
          textAlign = _ref4.textAlign;
      return /*#__PURE__*/React.createElement(Td, {
        key: key + "-" + j + "-" + field,
        textAlign: textAlign || DEFAULT_TEXT_ALIGN,
        loading: true
      });
    }));
  }))));
});
Table.displayName = 'Table';
Table.identifier = Table.displayName; // used in docs website

Table.clonableProps = SYSTEM_PROPS;
Table.defaultProps = {
  emptyIcon: Search,
  tableLayout: 'auto'
};
export default Table;